import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, PasswordInput } from '@medikei/ui';
import { AuthLayout, AuthHeader, FormFooter } from '@/components/auth';
import { authApi, AuthApiError } from '@/api/http-client';
import {
  passwordValidationRules,
  getPasswordStrength,
  isStrongPassword,
} from '@/utils/validation';
import {
  Lock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/utils/cn';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const passwordStrength = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError('Senha é obrigatória');
      return false;
    }
    if (!isStrongPassword(value)) {
      setPasswordError('Senha não atende aos requisitos mínimos');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const validateConfirmPassword = (value: string): boolean => {
    if (!value) {
      setConfirmError('Confirmação de senha é obrigatória');
      return false;
    }
    if (value !== password) {
      setConfirmError('As senhas não coincidem');
      return false;
    }
    setConfirmError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const isPasswordValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(confirmPassword);

    if (!isPasswordValid || !isConfirmValid) {
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(token!, password);
      setSuccess(true);
    } catch (err) {
      const error = err as Error;
      if (error instanceof AuthApiError) {
        if (error.code === 'INVALID_TOKEN') {
          setError('Link de recuperação expirado ou inválido. Solicite um novo.');
        } else if (error.code === 'TOKEN_EXPIRED') {
          setError('Link de recuperação expirado. Solicite um novo.');
        } else {
          setError(error.message || 'Erro ao redefinir senha');
        }
      } else {
        setError('Erro ao redefinir senha. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Senha Redefinida!
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Sua senha foi alterada com sucesso. Agora você pode fazer login
            com sua nova senha.
          </p>
          <Link to="/login">
            <Button className="w-full">Ir para Login</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (!token) {
    return null;
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center mb-6">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Lock size={28} className="text-primary" />
        </div>
      </div>

      <AuthHeader
        title="Redefinir Senha"
        description="Crie uma nova senha segura para sua conta"
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <PasswordInput
          label="Nova Senha"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError) validatePassword(e.target.value);
          }}
          onBlur={() => validatePassword(password)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          error={passwordError || undefined}
        />

        {password && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={cn('h-full transition-all', passwordStrength.color)}
                  style={{ width: `${passwordStrength.score}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {passwordStrength.label}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-1">
              {passwordValidationRules.map((rule) => {
                const passed = rule.test(password);
                return (
                  <div
                    key={rule.id}
                    className={cn(
                      'flex items-center gap-1.5 text-xs',
                      passed ? 'text-green-600' : 'text-gray-400'
                    )}
                  >
                    {passed ? (
                      <CheckCircle size={12} className="text-green-500" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-gray-300" />
                    )}
                    {rule.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <PasswordInput
          label="Confirmar Nova Senha"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (confirmError) validateConfirmPassword(e.target.value);
          }}
          onBlur={() => validateConfirmPassword(confirmPassword)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          error={confirmError || undefined}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          loading={isLoading}
        >
          {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
        </Button>

        <FormFooter>
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={14} />
            Voltar para Login
          </Link>
        </FormFooter>
      </form>
    </AuthLayout>
  );
}